/**
 * artworkPreviewState.js -- map an artwork_preview sidecar to a display
 * decision. The SINGLE source of the preview-state UI, shared by the
 * Decoration Library grid (DecorationLibraryList.vue) and the editor Stage
 * (Stage.vue) so the two surfaces render the four states identically.
 *
 * Plan: docs/planning/2026-07-16-decoration-file-formats-EXECUTION-PLAN.md
 * section 5.3 (grid) + 5.3b (Stage). The sidecar is exposed on the artwork
 * payload as ``preview = {state, preview_url, ...}`` (PR-4). A raster/SVG
 * artwork has NO sidecar row, so ``preview`` is ``null`` -- the "n/a, use
 * the existing thumbnail/Artifi render" signal.
 *
 * The five backend states (api/src/models/artwork_preview.py) collapse to
 * THREE user-facing display kinds:
 *
 *   - ``pending``  : a spinner tile + "Generating preview...". Covers
 *                    ``pending`` / ``rendering`` (render in progress) AND
 *                    ``failed_retryable`` (the retry is lazy on the next
 *                    view -- the user does not distinguish it from pending).
 *   - ``ready``    : render ``preview_url`` as the raster image.
 *   - ``failed``   : an honest, non-technical re-upload message for
 *                    ``failed_permanent`` (no stack trace, no error code,
 *                    no manual retry -- recourse is re-upload).
 *
 * A null/absent preview maps to ``none`` (the caller keeps its existing
 * fallback). An unknown or malformed state degrades to ``pending`` (fail
 * toward the record: show the spinner, not a broken tile).
 */

import { isEmbroiderySource } from "@/utils/artworkUploadContract";

// Backend state string constants (mirror
// api/src/models/artwork_preview.py PREVIEW_STATES).
export const PREVIEW_STATE_PENDING = "pending";
export const PREVIEW_STATE_RENDERING = "rendering";
export const PREVIEW_STATE_READY = "ready";
export const PREVIEW_STATE_FAILED_RETRYABLE = "failed_retryable";
export const PREVIEW_STATE_FAILED_PERMANENT = "failed_permanent";

// Non-terminal states drive the client poll -- a display re-kicks these on
// the next list load, so the client keeps polling until they resolve.
const NON_TERMINAL_STATES = new Set([
  PREVIEW_STATE_PENDING,
  PREVIEW_STATE_RENDERING,
  PREVIEW_STATE_FAILED_RETRYABLE,
]);

// User-facing copy for a permanently-failed render. Non-technical and
// actionable -- the only recourse is re-upload (no manual retry button).
export const FAILED_PREVIEW_MESSAGE =
  "We couldn't generate a preview for this file -- try re-uploading it.";

/**
 * Map a preview sidecar object to a display decision.
 *
 * @param {object|null} preview -- ``{state, preview_url}`` or null.
 * @returns {{kind: 'none'|'pending'|'ready'|'failed', previewUrl?: string,
 *            message?: string}}
 */
export function previewDisplay(preview) {
  if (!preview || typeof preview !== "object" || !preview.state) {
    return { kind: "none" };
  }
  const state = preview.state;
  if (state === PREVIEW_STATE_READY) {
    // A ready row must carry a preview_url; if it somehow does not, fall
    // back to the spinner rather than render a broken <img>.
    if (preview.preview_url) {
      return { kind: "ready", previewUrl: preview.preview_url };
    }
    return { kind: "pending" };
  }
  if (state === PREVIEW_STATE_FAILED_PERMANENT) {
    return { kind: "failed", message: FAILED_PREVIEW_MESSAGE };
  }
  // pending / rendering / failed_retryable AND any unknown future state ->
  // the spinner (forward-compatible; fail toward the record).
  return { kind: "pending" };
}

/**
 * True when a single preview is in a non-terminal state (still rendering or
 * awaiting a lazy retry) -- i.e. the client should keep polling it.
 *
 * @param {object|null} preview
 * @returns {boolean}
 */
export function isNonTerminalPreview(preview) {
  if (!preview || typeof preview !== "object" || !preview.state) return false;
  return NON_TERMINAL_STATES.has(preview.state);
}

/**
 * True when ANY row in a list carries a non-terminal preview -- the signal
 * to (keep) polling the list. Scoped so a list with only terminal / raster
 * rows never polls.
 *
 * @param {Array<{preview?: object|null}>} rows
 * @returns {boolean}
 */
export function hasNonTerminalPreview(rows) {
  if (!Array.isArray(rows)) return false;
  return rows.some((row) => isNonTerminalPreview(row && row.preview));
}

// ---------------------------------------------------------------------------
// "No preview for this file type" (EPS, and an AI/PDF that could not be
// rasterized). The backend decides and sends ``preview_unsupported: true``
// on the library and detail payloads (rule:
// api/src/services/artwork_format.preview_unsupported_for).
// ---------------------------------------------------------------------------

export const PREVIEW_KIND_UNSUPPORTED = "unsupported";

export const NO_PREVIEW_FILE_TYPE_MESSAGE = "No preview for this file type";

// What the user can do about it. An Illustrator file without PDF content
// is the common case and has a specific fix; everything else (EPS, a PDF
// that could not be drawn) needs a previewable version.
export const NO_PREVIEW_AI_HINT =
  "Save the .ai with Create PDF Compatible File, then re-upload it.";
export const NO_PREVIEW_GENERIC_HINT =
  "Upload a PNG, SVG or PDF version to see a preview.";

// Vector print formats. Their raw file is never an image a browser can draw;
// they display only through the server-rendered PNG stand-in (thumbnail_url).
const VECTOR_PRINT_EXTENSIONS = [".eps", ".ai", ".pdf"];

function isVectorPrintFile(name) {
  const lower = String(name || "").toLowerCase();
  return VECTOR_PRINT_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/**
 * Display decision for a whole artwork row: the file-type "no preview" state
 * first, otherwise the stitch-preview sidecar (``previewDisplay``).
 *
 * @param {object|null} artwork -- a library / detail artwork payload.
 * @returns {{kind: 'none'|'pending'|'ready'|'failed'|'unsupported',
 *            previewUrl?: string, message?: string, hint?: string}}
 */
export function artworkPreviewDisplay(artwork) {
  if (artwork && artwork.preview_unsupported === true) {
    const name = String(artwork.original_filename || artwork.filename || "");
    return {
      kind: PREVIEW_KIND_UNSUPPORTED,
      message: NO_PREVIEW_FILE_TYPE_MESSAGE,
      hint: name.toLowerCase().endsWith(".ai")
        ? NO_PREVIEW_AI_HINT
        : NO_PREVIEW_GENERIC_HINT,
    };
  }
  return previewDisplay(artwork && artwork.preview);
}

// A stitch file (.dst/.emb). ``image_url`` legitimately holds a stitch
// file's SOURCE BYTES, which are not an image: drawn into an <img> they
// render nothing. Checked on the URL path (a signed URL's query string
// stripped) as well as the filename, through the shared upload-contract
// predicate rather than a second extension list.
function isStitchSource(url, filename) {
  const path = String(url || "").split("?")[0];
  return isEmbroiderySource(path) || isEmbroiderySource(filename);
}

/**
 * The image to draw for an artwork with no stitch-preview sidecar: the
 * server thumbnail, else the source ``image_url`` -- except for a vector
 * print file (EPS/AI/PDF) or a stitch file (DST/EMB), whose raw file would
 * be a broken image. Those return null so the caller shows its placeholder
 * until a stand-in (thumbnail or rendered preview) exists.
 *
 * @param {object|null} artwork
 * @returns {string|null}
 */
export function artworkThumbnailSrc(artwork) {
  if (!artwork) return null;
  if (artwork.thumbnail_url) return artwork.thumbnail_url;
  const name = artwork.original_filename || artwork.filename;
  if (isVectorPrintFile(name)) return null;
  if (isStitchSource(artwork.image_url, name)) return null;
  return artwork.image_url || null;
}

/**
 * THE image an off-product artwork tile draws, or null for the caller's
 * placeholder. The single rule shared by every surface that shows an
 * artwork as a picture (Decoration Library grid, Add placement wizard,
 * the product editor's artwork picker, the store editor's Decorations
 * rows): a stitch file's READY rendered preview first, else
 * ``artworkThumbnailSrc``. It never returns a raw stitch or vector file.
 *
 * The caller supplies ``artwork.preview`` (the ``artwork_preview`` sidecar,
 * ``{state, preview_url}``). ``GET /api/artworks/library`` and the store
 * decoration rollup carry it on the row; a SAFRS ``/api/artworks/`` row
 * cannot (its rollup commits the lazy render kick), so SAFRS consumers ask
 * ``POST /api/artworks/preview_states`` and stamp it on.
 *
 * @param {object|null} artwork
 * @returns {string|null}
 */
export function artworkImageSrc(artwork) {
  const display = artworkPreviewDisplay(artwork);
  if (display.kind === "ready") return display.previewUrl;
  return artworkThumbnailSrc(artwork);
}

/**
 * ``artworkImageSrc`` for a store decoration-rollup row
 * (``GET /api/stores/<id>/decoration_rollup/``), whose artwork name fields
 * are prefixed ``artwork_``. The rollup stamps the decoration's pinned
 * version's ``preview`` on the row.
 *
 * @param {object|null} row
 * @returns {string|null}
 */
export function decorationRowImageSrc(row) {
  if (!row) return null;
  return artworkImageSrc({
    thumbnail_url: row.thumbnail_url,
    image_url: row.image_url,
    preview: row.preview,
    original_filename: row.artwork_original_filename,
    filename: row.artwork_filename,
  });
}
